<template>
  <div class="message-container">
    <!-- Row for Sender Name and Color -->
    <div class="sender-row">
      <span
        v-if="getSenderName() || isCurrentUser"
        class="sender-name"
        :style="{ color: senderColor }"
      >
        {{ isCurrentUser ? $t('CHAT_LIST.YOU') : getSenderName() }}
      </span>
      <span v-if="shouldShowSenderPhoneNumber" :style="senderPhoneStyle">
        {{ senderPhoneNumber }}
      </span>
    </div>

    <!-- Row for Content and Previews -->
    <div class="content-row">
      <span
        v-if="message.content && !message.attachments"
        class="message-text message-content"
      >
        {{ parsedLastMessage }}
      </span>
      <span
        v-else-if="message.content && message.attachments"
        class="message-text"
      >
        <fluent-icon
          v-if="attachmentIcon && showMessageType"
          size="16"
          class="icon-attachment"
          :icon="attachmentIcon"
        />
        <span class="message-content"> {{ parsedLastMessage }} </span>
      </span>

      <!-- Solo adjuntos -->
      <span v-else-if="message.attachments" class="message-text">
        <fluent-icon
          v-if="attachmentIcon && showMessageType"
          size="16"
          class="icon-attachment"
          :icon="attachmentIcon"
        />
        <span v-if="isFile" class="message-content">
          {{ decodedFileName }}
        </span>
        <span v-else-if="isAudio" class="message-content">
          {{ audioDuration }}
        </span>
        <span v-else class="message-content">
          {{ $t(attachmentMessageContent) }}
        </span>
      </span>
      <!-- Mensaje vacío -->
      <span v-else class="message-text message-content">
        {{ defaultEmptyMessage || $t('CHAT_LIST.NO_CONTENT') }}
      </span>
    </div>
  </div>
</template>

<script>
import { isAWhatsAppLineChannel } from 'dashboard/helper/messageHelper';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import { ATTACHMENT_ICONS } from 'shared/constants/messages';
import { mapGetters } from 'vuex';

export default {
  name: 'MessagePreview',
  mixins: [messageFormatterMixin],
  props: {
    message: {
      type: Object,
      required: true,
    },
    showMessageType: {
      type: Boolean,
      default: true,
    },
    defaultEmptyMessage: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      audioDuration: null,
      audioObject: null,
      isWhatsAppChannel: false,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentChat: 'getSelectedChat',
    }),
    /** Indica si el chat actual es un grupo. */
    isGroupChat() {
      return typeof this.currentChat.is_group === 'boolean'
        ? this.currentChat.is_group
        : null;
    },
    // La informacion del canal se puede obtener de estas dos formas, depende el mensaje ya existe o si recien se creo el objeto tiene una estructura distinta
    channelType() {
      return this.currentChat.meta.channel || this.currentChat.channel;
    },
    /** Define estilos para el número de teléfono del remitente. */
    senderPhoneStyle() {
      return {
        fontWeight: '400',
        fontSize: '12px',
        lineHeight: '20px',
        letterSpacing: '0px',
        color: this.senderColor,
      };
    },
    // Verifica si el archivo adjunto es un audio
    isAudio() {
      return this.lastMessageFileType === 'audio';
    },

    /** Retorna el color del remitente o '#000' si no está definido, usando 'unknown_user' para sender nulo. */
    senderColor() {
      const sender = this.message.sender;
      const participantsColors = this.currentChat.participants_colors;

      if (!participantsColors) return '#000';
      const participantKey = sender
        ? `${
            sender.type?.toLowerCase() === 'agent_bot'
              ? 'agentbot'
              : sender.type?.toLowerCase()
          }_${sender.id}`
        : 'unknown_user';
      return participantsColors[participantKey] || '#000';
    },
    // Obtiene el tipo de archivo del último mensaje en el chat
    lastMessageFileType() {
      const [{ file_type: fileType } = {}] = this.message.attachments || [];
      return fileType;
    },

    /**
     * Obtiene el contenido parseado del último mensaje en el chat.
     * @returns {string} - El contenido parseado del último mensaje.
     */
    parsedLastMessage() {
      const { content_attributes: contentAttributes } = this.message;
      const { email: { subject } = {} } = contentAttributes || {};
      return this.getPlainText(subject || this.message.content);
    },

    /**
     * Obtiene el icono de adjunto para el último mensaje en el chat.
     * @returns {string} - El icono de adjunto correspondiente al tipo de archivo del último mensaje.
     */
    attachmentIcon() {
      return ATTACHMENT_ICONS[this.lastMessageFileType] || '';
    },

    /**
     * Obtiene el contenido del mensaje de adjunto.
     * @returns {string} - El contenido del mensaje de adjunto.
     */
    attachmentMessageContent() {
      return `CHAT_LIST.ATTACHMENTS.${this.lastMessageFileType}.CONTENT`;
    },
    // Verifica si el archivo adjunto es un archivo
    isFile() {
      return this.lastMessageFileType === 'file';
    },
    /**
     * Obtiene el nombre del archivo adjunto y lo decodifica.
     * @returns {string} - El nombre del archivo adjunto, decodificado.
     */
    decodedFileName() {
      const attachment = this.message.attachments[0];
      if (attachment && attachment.data_url) {
        const url = attachment.data_url;
        const fileNameWithExtension = url.substring(url.lastIndexOf('/') + 1);
        return decodeURIComponent(fileNameWithExtension);
      }
      return '';
    },
    /** Comprueba si el mensaje fue enviado por el usuario actual. */
    isCurrentUser() {
      return (
        this.message.sender?.id === this.currentUser.id &&
        this.message.sender?.type === 'user'
      );
    },
    /** Determina si se debe mostrar el número de teléfono del remitente. */
    shouldShowSenderPhoneNumber() {
      return (
        this.isGroupChat &&
        this.senderPhoneNumber &&
        this.senderPhoneNumber !== this.getSenderName()
      );
    },
    /** Obtiene el número de teléfono del remitente. */
    senderPhoneNumber() {
      return this.message?.sender?.phone_number || '';
    },
    // Canal de línea de WhatsApp: sin sender se atribuye al teléfono de la línea (messageHelper).
    isAWhatsAppLineChannel() {
      return isAWhatsAppLineChannel(this.channelType);
    },
    // Obtiene el canal actual del chat.
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.message.inbox_id);
    },
  },
  watch: {
    'message.attachments': 'loadAudioDuration',
  },
  mounted() {
    this.loadAudioDuration();
  },
  beforeDestroy() {
    if (this.audioObject) {
      this.audioObject.pause();
      this.audioObject = null;
    }
  },
  methods: {
    // Comprueba si el mensaje no tiene un remitente o si es un mensaje externo
    fromExternalMessage() {
      return (
        !this.message.sender || Object.keys(this.message.sender).length === 0
      );
    },
    /** Obtiene el nombre del remitente, aplicando lógica para bots y mensajes externos. */
    getSenderName() {
      const customSender = this.message.content_attributes?.custom_sender || {};
      const sender = this.message.sender || {};
      const isSentByBot = sender.type === 'agent_bot';
      let name = sender?.name || customSender.name;
      if (isSentByBot) {
        return `${this.$t('CONVERSATION.BOT')}`;
      }

      if (this.fromExternalMessage()) {
        if (this.isAWhatsAppLineChannel) {
          name = this.inbox.phone_number;
        } else {
          name = this.$t('CONVERSATION.FROM_EXTERNAL_MESSAGE');
        }
      }
      return name;
    },
    /**
     * Carga la duración del archivo de audio.
     */
    loadAudioDuration() {
      if (this.isAudio && this.message.attachments.length > 0) {
        const audioUrl = this.message.attachments[0].data_url;

        if (this.audioObject) {
          this.audioObject.pause();
        }

        this.audioObject = new Audio(audioUrl);

        this.audioObject.oncanplaythrough = () => {
          const duration = this.audioObject.duration;
          const minutes = Math.floor(duration / 60);
          const seconds = Math.floor(duration % 60);
          this.audioDuration = `${minutes}:${
            seconds < 10 ? '0' : ''
          }${seconds}`;
        };

        this.audioObject.onerror = () => {
          this.audioDuration = '00:00';
        };
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sender-name {
  font-weight: 600;
}
.message-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 40px;
}

.sender-row {
  align-items: center;
}

.content-row {
  display: flex;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  position: relative;
}

.message-text {
  display: inline-flex;
  align-items: flex-start;
  flex: 1;
  max-width: 100%;
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-normal);
  line-height: var(--space-normal);
}

.icon-attachment {
  margin-right: 0.4rem;
  flex-shrink: 0;
  align-self: flex-start;
}

.message-content {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1; /* Limitar a dos líneas */
  overflow: hidden; /* Ocultar el texto que se excede */
}
</style>
